import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from './authContext';
import { api } from './lib/api';
import Logo from './logo.jpg';
import './App.css';
import { FaSignOutAlt, FaUser, FaRobot, FaWallet, FaDollarSign, FaMoneyBill } from 'react-icons/fa';

const isLocal = window.location.hostname === 'localhost';
const baseUrl = isLocal ? 'http://localhost:5000' : 'https://www.trendscout.club';

function Header() {
  const { isAuthenticated, setIsAuthenticated } = useContext(AuthContext);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const userId = localStorage.getItem('userId');         // already used elsewhere in your app
  const activeBotId = localStorage.getItem('activeBotId'); // set from AdminFundSharing (or wherever you pick bots)

  const [entitlement, setEntitlement] = useState(null);
  const [entitlementTip, setEntitlementTip] = useState('');

  const navigate = useNavigate();

  // sticky header
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  // 🔥 Fetch entitlement (sum over all bots) when logged in
  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setEntitlement(null);
      setEntitlementTip('');
      return;
    }
    (async () => {
      try {
        const res = await api.get('/api/fund-sharing/entitlement', {
          params: { userId }
        });
        const total = Number(res.data?.total || 0);
        setEntitlement(total);

        // Simple tooltip with a per-bot breakdown (optional)
        const lines = (res.data?.breakdown || [])
          .map(b => `${b.name} (${b.exchange}): $${(b.entitlement ?? 0).toFixed(2)} — ${b.sharePct?.toFixed?.(2) ?? b.sharePct}% share`)
          .join('\n');
        setEntitlementTip(lines);
      } catch {
        setEntitlement(null);
        setEntitlementTip('');
      }
    })();
  }, [isAuthenticated, userId]);



  const toggleMenu = () => setIsMenuOpen(v => !v);
  const toggleSubmenu = () => setSubmenuOpen(v => !v);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    navigate('/');
  };





  return (
    <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
      <div className="header-content">
        <div className="header-left">
          <img src={Logo} alt="Logo" className="logo" />
          <h1>Trend Scout</h1>
          {/* entitlement pill (sum over all bots) */}
          {isAuthenticated && entitlement != null && (
            <div
              className="entitlement-pill"
              title={entitlementTip || 'Click to view details'}
              onClick={() => navigate('/me/funds')}
            >
              ${entitlement.toFixed(2)}
            </div>
          )}
        </div>

        <div className={`hamburger ${isMenuOpen ? 'open' : ''}`} onClick={toggleMenu}>
          <div className="line"></div>
          <div className="line"></div>
          <div className="line"></div>
        </div>

        <nav className={`nav-menu ${isMenuOpen ? 'open' : ''}`}>
          {isAuthenticated ? (
            <>
              <div className="submenu-title"><Link to="/" onClick={() => setIsMenuOpen(false)}>Home</Link></div>
              <div className="submenu-title"><Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>Dashboard</Link></div>
              <div className="submenu-title"><Link to="/hyped-alts" onClick={() => setIsMenuOpen(false)}>Hype</Link></div>

              <div className="submenu-container">
                <span className="submenu-title" onClick={toggleSubmenu}>Markets</span>
                {submenuOpen && (
                  <div className="submenu">
                    <Link to="/precious-metals" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Metals</Link>
                    <Link to="/macro-regime" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Macro</Link>         
                    <Link to="/market-events" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Events</Link> 
                  </div>
                )}
              </div>

              <div className="submenu-container">
                <span className="submenu-title" onClick={toggleSubmenu}>Alts</span>
                {submenuOpen && (
                  <div className="submenu">
                    <Link to="/security-check" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Audit</Link>
                    <Link to="/ai-trades" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Trades</Link>
                    {userId === 'pziPDtOgJiX06oGuJxNwxE' && (
                      <>
                        <Link to="/ai-token-analysis" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Single Token</Link>
                        <Link to="/ai-update-tokens" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Update Tokens</Link>
                        <Link to="/ai-recommendation-stats" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Stats Admin</Link>
                        <Link to="/market-pulse/admin" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Market Pulse</Link>
                      </>
                    )}
                    <Link to="/ai-stats" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Ai Stats</Link>
                    <Link to="/ai-track-record" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Ai Track Record</Link>
                  </div>
                )}
              </div>
                  
                    {userId === 'pziPDtOgJiX06oGuJxNwxE' && (
                      <div className="submenu-container">
                        <span className="submenu-title" onClick={toggleSubmenu}>Bots</span>
                        {submenuOpen && (
                          <div className="submenu">
                            <Link 
                            className='subMenu-item'
                            to="/admin/fund-sharing" onClick={() => setIsMenuOpen(false)}>
                            Funding<FaDollarSign size={24} color="white" />
                            </Link>
                            <Link
                              className='subMenu-item'
                              to="/binance/bot-settings"
                              onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}
                            >
                              CRG<FaRobot size={24} color="white" />
                            </Link>
                            <Link
                              className='subMenu-item'
                              to="/bitget/bot-settings"
                              onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}
                            >
                              Bitget<FaRobot size={24} color="white" />
                            </Link>
                            <Link
                              className='subMenu-item'
                              to="/gytis/bot-settings"
                              onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}
                            >
                              Modesta<FaRobot size={24} color="white" />
                            </Link>
                            <Link
                              className='subMenu-item'
                              to="/crg-wallet"
                              onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}
                            >
                              CRG<FaWallet size={24} color="white" />
                            </Link>
                           <Link
                              className='subMenu-item'
                              to="/bitget-bot-wallet"
                              onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}
                            >
                              Bitget <FaWallet size={24} color="white" />
                            </Link>
                            <Link
                              className='subMenu-item'
                              to="/gytis-bot-wallet"
                              onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}
                            >
                              Modesta<FaWallet size={24} color="white" />
                            </Link>
                          </div>
                        )}
                      </div>
                    )}

                    {userId !== 'pziPDtOgJiX06oGuJxNwxE' && userId !== '54da2ea3-08ba-4e30-8a94-71ce6fd936dd' && (
                      <div className="submenu-container">
                        <span className="submenu-title" onClick={toggleSubmenu}>Bots</span>
                        {submenuOpen && (
                          <div className="submenu">
                            <Link 
                            className='subMenu-item'
                            to="/me/funds" onClick={() => setIsMenuOpen(false)}>
                            Funds<FaDollarSign size={24} color="white" />
                            </Link>
                            <Link
                              className='subMenu-item'
                              to="/crg-wallet"
                              onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}
                            >
                              CRG<FaWallet size={24} color="white" />
                            </Link>
                          </div>
                        )}
                      </div>
                    )}


                  {/* GYTIS STARTS  */}
                  {userId === '54da2ea3-08ba-4e30-8a94-71ce6fd936dd' && ( 
                      <div className="submenu-container">
                        <span className="submenu-title" onClick={toggleSubmenu}>Bots</span>
                        {submenuOpen && (
                          <div className="submenu">
                            <Link 
                            className='subMenu-item'
                            to="/me/funds" onClick={() => setIsMenuOpen(false)}>
                            Funds<FaDollarSign size={24} color="white" />
                            </Link>
                            <Link
                              className='subMenu-item'
                              to="/gytis/bot-settings"
                              onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}
                            >
                              Settings<FaRobot size={24} color="white" />
                            </Link>
                            <Link
                              className='subMenu-item'
                              to="/gytis-bot-wallet"
                              onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}
                            >
                              Wallet<FaWallet size={24} color="white" />
                            </Link>
                          </div>
                        )}
                      </div>
                    )}

              <div className="submenu-container">
                <span className="submenu-title" onClick={toggleSubmenu}>Memes</span>
                {submenuOpen && (
                  <div className="submenu">
                    <Link to="/alltokens" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Current Tokens</Link>
                    {userId === 'pziPDtOgJiX06oGuJxNwxE' && (
                      <>
                        <Link to="/all-token-data" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Current Data</Link>
                      </>
                    )}
                    <Link to="/historical-tokens" onClick={() => setIsMenuOpen(false)}>All Tokens</Link>
                    {userId === 'pziPDtOgJiX06oGuJxNwxE' && (
                      <>
                        <Link to="/historical-token-data" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>All Data</Link>
                      </>
                    )}
                    {(userId === 'pziPDtOgJiX06oGuJxNwxE' || userId === '84e2208f-e4c5-45c4-a68b-97e429b20206') && (
                      <>
                        <Link to="/stored-tokens" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Stored Tokens</Link>
                      </>
                    )}
                    {userId === 'pziPDtOgJiX06oGuJxNwxE' && (
                      <>
                        <Link to="/stored-token-data" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Stored Data</Link>
                      </>
                    )}
                  </div>
                )}
              </div>

              {userId !== 'pziPDtOgJiX06oGuJxNwxE' ? (
                <div className="submenu-container">
                  <span className="submenu-title" onClick={toggleSubmenu}>Tools</span>
                  {submenuOpen && (
                    <div className="submenu">
                      <Link to="/security-check" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Audit</Link>
                      <Link to="/performance-sim" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Simulation</Link>
                      <Link to="/prediction" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Prediction</Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="submenu-container">
                  <span className="submenu-title" onClick={toggleSubmenu}>Tools</span>
                  {submenuOpen && (
                    <div className="submenu">
                      <Link to="/ai-coach" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>AI Coach</Link>
                      <Link to="/security-check" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Audit</Link>
                      <Link to="/performance-sim-all" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Simulation</Link>
                      <Link to="/prediction" onClick={() => { setIsMenuOpen(false); setSubmenuOpen(false); }}>Prediction</Link>
                    </div>
                  )}
                </div>
              )}

              <div className="logout-button" onClick={handleLogout}>
                <FaSignOutAlt size={24} color="white" />
                <span className="submenu-title">Logout</span>
              </div>
            </>
          ) : (
            <div className="login-button">
              <Link to="/login">
                <FaUser className='fauser' size={20} color="white" />
                <span className="submenu-title">Login / Signup</span>
              </Link>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
}

export default Header;
